import effects from 'app/effects/effects'
import * as R from 'ramda'
import moment from 'moment'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { usePermissionInfo } from 'app/auth/useAccessControl'
import { arrayAsHookDependencies } from 'libs/hook'
import { networkErrorToErrorType, statusToErrorType } from 'commons/error'
import { TYPE_GENERAL, TYPE_EVENT } from 'app/constants/messageType'

export const NotificationListType = {
  NORMAL_NOTIFICATION: '1',
  SCHEDULED_NOTIFICATION: '2',
  NORMAL_AND_SCHEDULED_NOTIFICATION: '',
}

const VOUCHER_TYPE = 3

export const isShowScheduledNotification = (type) => {
  return type === NotificationListType.NORMAL_AND_SCHEDULED_NOTIFICATION
}

export const NOTIFICATIONS_PAGE_SIZE = 10
export const FIRST_PAGE = 1
export const SINGLE_ITEM = 1

const SCHEDULE_VIEW = '1'
const SCHEDULE_HIDE = '0'
const CREATE_BY_ME_VIEW = '1'
const FILTER_UNSENT_VIEW = '1'
const FILTER_UNSENT_HIDE = '0'
const FILTER_EXPIRING_VIEW = '1'
const FILTER_EXPIRING_HIDE = '0'
const FILTER_EXPIRED_VIEW = '1'
const FILTER_EXPIRED_HIDE = '0'

const initialState = {
  currentPage: FIRST_PAGE,
  hasMore: true,
  data: {},
  isLoading: false,
  isRefetching: false,
  error: undefined,
  fetchedAt: '',
  orderDataIds: [],
  notificationListType: NotificationListType.NORMAL_NOTIFICATION,
  hasScheduledNotification: false,
  filter: {
    startDate: null,
    endDate: null,
    general: false,
    event: false,
    voucher: false,
    createdByMe: false,
    showSchedule: false,
    createdDate: 'DESC',
    unsentFilter: false,
    expiring: false,
    expired: false,
  },
}

export const ActionType = {
  INCREMENT_PAGE: 'INCREMENT_PAGE',
  REFETCH: 'REFETCH',
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  FETCH_PERMISSION_DENIED: 'FETCH_PERMISSION_DENIED',
  FETCH_FINAL: 'FETCH_FINAL',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  REFETCH_PAGE: 'REFETCH_PAGE',
  HAS_SCHEDULED: 'HAS_SCHEDULED',
  NO_SCHEDULED: 'NO_SCHEDULED',
  SUBMIT_FILTER: 'SUBMIT_FILTER',
  ON_SORT_TIMELINE: 'ON_SORT_TIMELINE',
  REFETCH_BY_ID_START: 'REFETCH_BY_ID_START',
  REFETCH_BY_ID_END: 'REFETCH_BY_ID_END',
  UPDATE_DISPLAY_HISTORY_BY_ID: 'UPDATE_DISPLAY_HISTORY_BY_ID',
  LOADING_STOP: 'LOADING_STOP',
}

const mapToObjWithKey = (key) => {
  return R.reduce((acc, content) => ({ ...acc, [key(content)]: content }), {})
}

const parseContent = (state, action) => {
  const actionMapObj = R.compose(
    mapToObjWithKey(R.prop('id')),
    R.prop('payload'),
  )(action)

  const actionIds = R.compose(
    R.reduce((acc, content) => [...acc, R.prop('id', content)], []),
    R.prop('payload'),
  )(action)

  if (state.isRefetching === true) {
    return { orderDataIds: actionIds, data: actionMapObj }
  } else {
    return {
      orderDataIds: state.orderDataIds.concat(actionIds),
      data: { ...state.data, ...actionMapObj },
    }
  }
}

const notificationsReducer = (state, action) => {
  switch (action.type) {
    case ActionType.REFETCH:
      return {
        ...state,
        fetchedAt: moment(),
        currentPage: FIRST_PAGE,
        hasMore: true,
        isRefetching: true,
      }
    case ActionType.REFETCH_PAGE:
      return {
        ...state,
        fetchedAt: moment(),
        hasMore: true,
        currentPage: action.payload,
      }
    case ActionType.INCREMENT_PAGE:
      return {
        ...state,
        currentPage: R.inc(state.currentPage),
      }
    case ActionType.FETCH_INIT:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      }
    case ActionType.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isRefetching: false,
        error: undefined,
        ...parseContent(state, action),
      }
    case ActionType.FETCH_FINAL:
      return {
        ...state,
        isLoading: false,
        hasMore: false,
        error: undefined,
        isRefetching: false,
        ...parseContent(state, action),
      }
    case ActionType.FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        hasMore: false,
        isRefetching: false,
        error: action.payload,
      }
    case ActionType.FETCH_PERMISSION_DENIED:
      return {
        ...state,
        isLoading: false,
        hasMore: false,
        isRefetching: false,
        error: action.payload,
        data: [], // always clear information when user don't have permission to view
      }
    case ActionType.ADD_NOTIFICATION:
      return {
        ...state,
        data: [action.payload, ...state.data],
      }
    case ActionType.UPDATE_NOTIFICATION:
      // eslint-disable-next-line no-case-declarations
      const updatedContent = R.over(
        R.lensProp('subscribers'),
        R.defaultTo([]),
        action.payload,
      )
      // eslint-disable-next-line no-case-declarations
      const updatedId = R.prop('id', action.payload)
      return {
        ...state,
        data: R.compose(
          R.set(R.lensProp(updatedId), updatedContent),
          R.prop('data'),
        )(state),
      }

    case ActionType.HAS_SCHEDULED:
      return {
        ...state,
        hasScheduledNotification: action.payload.hasScheduledNotification,
      }
    case ActionType.SUBMIT_FILTER:
      return {
        ...state,
        fetchedAt: moment(),
        currentPage: FIRST_PAGE,
        hasMore: true,
        isRefetching: true,
        filter: {
          ...state.filter,
          startDate: R.isNil(action.payload.startDate)
            ? null
            : moment(action.payload.startDate).startOf('day').format(),
          endDate: R.isNil(action.payload.endDate)
            ? null
            : moment(action.payload.endDate).endOf('day').format(),
          general: action.payload.general,
          event: action.payload.event,
          voucher: action.payload.voucher,
          createdByMe: action.payload.createdByMe,
          showSchedule: action.payload.showSchedule,
          unsentFilter: action.payload.unsentFilter,
          expiring: action.payload.expiring,
          expired: action.payload.expired,
        },
      }
    case ActionType.ON_SORT_TIMELINE:
      return {
        ...state,
        fetchedAt: moment(),
        currentPage: FIRST_PAGE,
        hasMore: true,
        isRefetching: true,
        filter: {
          ...state.filter,
          createdDate: action.payload,
        },
      }
    case ActionType.REFETCH_BY_ID_START:
      return {
        ...state,
        isLoading: true,
      }
    case ActionType.LOADING_STOP:
      return {
        ...state,
        isLoading: false,
      }
    case ActionType.REFETCH_BY_ID_END:
      // eslint-disable-next-line no-case-declarations
      const notification = action.payload.notification
      if (!notification) return { ...state, isLoading: false }

      // eslint-disable-next-line no-case-declarations
      const orderDataIds = state.orderDataIds
      return {
        ...state,
        isLoading: false,
        orderDataIds: orderDataIds.includes(notification.id)
          ? orderDataIds
          : [notification.id].concat(orderDataIds),
        data: {
          ...state.data,
          [notification.id]: notification,
        },
      }
    case ActionType.UPDATE_DISPLAY_HISTORY_BY_ID:
      return {
        ...state,
        data: action.payload,
      }
    default:
      throw new Error('Not supported action')
  }
}

const dateToUtc = (date) => moment(date).utc().format('YYYY-MM-DD HH:mm:ss')

export const useNotifications = () => {
  const [state, dispatchNotifications] = useReducer(
    notificationsReducer,
    initialState,
  )

  const getStartDate = R.compose(
    R.objOf('startDate'),
    R.ifElse(R.isNil, R.always(null), dateToUtc),
    R.path(['filter', 'startDate']),
  )

  const getEndDate = R.compose(
    R.objOf('endDate'),
    R.ifElse(R.isNil, R.always(null), dateToUtc),
    R.path(['filter', 'endDate']),
  )

  const mapNotificationType = (data) => {
    let typeArray = []
    if (data.filter.general) {
      typeArray.push(TYPE_GENERAL)
    }
    if (data.filter.event) {
      typeArray.push(TYPE_EVENT)
    }
    if (data.filter.voucher) {
      typeArray.push(VOUCHER_TYPE)
    }

    return R.ifElse(
      R.isEmpty,
      R.always({}),
      R.o(R.objOf('type'), R.join(',')),
    )(typeArray)
  }

  const mapCreatedByMe = R.ifElse(
    R.path(['filter', 'createdByMe']),
    R.o(R.objOf('createdByMe'), R.always(CREATE_BY_ME_VIEW)),
    R.always({}),
  )

  const mapUnsentFilter = R.ifElse(
    R.path(['filter', 'unsentFilter']),
    R.o(R.objOf('unsentFilter'), R.always(FILTER_UNSENT_VIEW)),
    R.o(R.objOf('unsentFilter'), R.always(FILTER_UNSENT_HIDE)),
  )

  const mapExpiringFilter = R.ifElse(
    R.path(['filter', 'expiring']),
    R.o(R.objOf('expiring'), R.always(FILTER_EXPIRING_VIEW)),
    R.o(R.objOf('expiring'), R.always(FILTER_EXPIRING_HIDE)),
  )

  const mapExpiredFilter = R.ifElse(
    R.path(['filter', 'expired']),
    R.o(R.objOf('expired'), R.always(FILTER_EXPIRED_VIEW)),
    R.o(R.objOf('expired'), R.always(FILTER_EXPIRED_HIDE)),
  )

  const apiFetchersCallback = useCallback(
    (dataState) => {
      effects.apis
        .getNotifications({
          page: dataState.currentPage,
          limit: NOTIFICATIONS_PAGE_SIZE,
          sort: `created_date:${dataState.filter.createdDate}`,
          schedule: dataState.filter.showSchedule
            ? SCHEDULE_VIEW
            : SCHEDULE_HIDE,
          ...getStartDate(dataState),
          ...getEndDate(dataState),
          ...mapNotificationType(dataState),
          ...mapCreatedByMe(dataState),
          ...mapUnsentFilter(dataState),
          ...mapExpiringFilter(dataState),
          ...mapExpiredFilter(dataState),
        })
        .then(({ data: newNotifications, meta }) => {
          const isLastPage = R.converge(R.equals, [
            R.prop('lastPage'),
            R.prop('currentPage'),
          ])
          if (isLastPage(meta) || !R.prop('lastPage', meta)) {
            dispatchNotifications({
              type: ActionType.FETCH_FINAL,
              payload: newNotifications,
            })
          } else {
            dispatchNotifications({
              type: ActionType.FETCH_SUCCESS,
              payload: newNotifications,
            })
          }
        })
        .catch((err) => {
          if (
            R.equals(statusToErrorType(403, 403), networkErrorToErrorType(err))
          )
            dispatchNotifications({
              type: ActionType.FETCH_PERMISSION_DENIED,
              payload: err,
            })
          dispatchNotifications({
            type: ActionType.FETCH_FAILURE,
            payload: err,
          })
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.currentPage, state.notificationListType],
  )

  const refetchNotifications = () => {
    dispatchNotifications({ type: ActionType.REFETCH })
  }

  const refetchNotificationById = (id) => {
    dispatchNotifications({ type: ActionType.REFETCH_BY_ID_START, id })
    effects.apis
      .getNotificationById(id)
      .then((notification) => {
        dispatchNotifications({
          type: ActionType.REFETCH_BY_ID_END,
          payload: { notification },
        })
      })
      .catch(() => {
        dispatchNotifications({
          type: ActionType.REFETCH_BY_ID_END,
          payload: { notification: undefined },
        })
      })
  }

  useEffect(() => {
    if (state.hasMore) {
      dispatchNotifications({ type: ActionType.FETCH_INIT })
      apiFetchersCallback(state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetchersCallback, state.hasMore, state.fetchedAt])

  const loadMore = () => {
    dispatchNotifications({ type: ActionType.INCREMENT_PAGE })
  }

  const fetchCurrentPage = () => {
    dispatchNotifications({
      type: ActionType.REFETCH_PAGE,
      payload: state.currentPage,
    })
  }

  const updateNotification = useCallback(
    (updatedNotification) => {
      let mapDataCancel = updatedNotification
      const notificationById = state.data[updatedNotification.id]
      const scheduleCancel = !R.isNil(updatedNotification.scheduleCancelDate)
      if (scheduleCancel) {
        mapDataCancel = {
          ...updatedNotification,
          scheduleDate: notificationById.scheduleDate,
          scheduleTimezone: notificationById.scheduleTimezone,
        }
      }
      dispatchNotifications({
        type: ActionType.UPDATE_NOTIFICATION,
        payload: mapDataCancel,
      })
    },
    [state.data, dispatchNotifications],
  )

  const updateDisplayHistory = (displayHistory, notificationId) => {
    let notificationById = { ...state.data[notificationId], displayHistory }

    dispatchNotifications({
      type: ActionType.UPDATE_DISPLAY_HISTORY_BY_ID,
      payload: { ...state.data, [notificationId]: notificationById },
    })
  }

  return [
    {
      state,
      loadMore,
      ActionType,
      refetchNotifications,
      refetchNotificationById,
      fetchCurrentPage,
      updateNotification,
      updateDisplayHistory,
    },
    dispatchNotifications,
  ]
}

export const useNotificationsAccessControl = (onPermissionInfoChange) => {
  const permissionInfo = usePermissionInfo()
  const [permissionChangeCount, setPermissionChangeCount] = useState(0)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Not call this when access notification timeline first time
    // for avoid duplicated notifications fetch
    if (permissionChangeCount > 0) onPermissionInfoChange()
    setPermissionChangeCount(R.inc)
  }, [serializePermissionInfo(permissionInfo)])
  /* eslint-enable react-hooks/exhaustive-deps */
}

const serializePermissionInfo = (permissionInfo) =>
  R.compose(
    R.join(''),
    R.map(arrayAsHookDependencies),
  )([R.prop('permissions', permissionInfo), R.prop('groups', permissionInfo)])
